import { useState, useEffect, useContext } from 'react'

import moment from 'moment'
import { useSelector } from 'react-redux'
import { isArray } from 'lodash'

import { connect, mapStateToProps, mapDispatchToProps } from '../../../reducers/Dispatchers'
import { canOnlyEditEventCode, createShiftOrConfigureIt, getShiftByDayAndInternship } from './helper'
import { getMultiSelectionShiftEncapsulation } from '../../../utils/entities/ShiftEncapsulation'
import { MultiSelectModes } from './constants'
import SimpleHeaderCell from './tableCells/SimpleHeaderCell'
import PausesHeaderCell from './tableCells/PausesHeaderCell'
import TotalHeaderCell from './tableCells/TotalHeaderCell'
import Line from './ShiftTableParts/Line'
import { fetchSchoolOptions } from '../../../utils/api/school'
import { LanguageTags, SchoolOptions } from '../../../utils/constants'
import { isObject } from '../../../utils'
import { GlobalContext } from '../../../Providers/GlobalProvider'
import { selectUser } from '../../../reducers/UserReducer'
import { isNurse, isSupervisor } from '../../../utils/roles'
import { SelectContext } from '../../../Providers/SelectProvider'
import { selectTranslate } from '../../../reducers/LocaleReducer/LocalReducer'
import LeftFloatingCheckbox from '../../antd/Checkbox/LeftFloatinCheckbox'

const { SUPERVISORS_EDIT_SCHEDULE } = SchoolOptions

const DATE_FORMAT_TYPE = 'DD/MM'
const { COLUMN } = MultiSelectModes

const InternshipsShiftsEditor = ({
  endDate, internships, getPauseQuotasCounts, getSelectedSector, getShiftsCounts, inWidgetMode, loading, multiSelectionMode, selectedShifts, startDate,
  addToMultiSelection, multiSelectionAddingHandler, onClickedDraft, onDeleteSchedule, onDesiderataSelect, onInternshipChecked, onInternshipSelected,
  onOpenChatForInternship, onShiftsWidgetCoverClick, onStudentSelected, removeFromMultiSelection, setIntershipToValidate
}) => {
  const { isReadOnly } = useContext(GlobalContext)
  const { selected, setSelected } = useContext(SelectContext)

  const t = useSelector(selectTranslate)
  const user = useSelector(selectUser)

  const [numberOfDays, setNumberOfDays] = useState(0)
  const [supervisorsEditSchedule, setSupervisorsEditSchedule] = useState(false)

  useEffect(() => {
    if (endDate && startDate) {
      setNumberOfDays(endDate.diff(startDate, 'days') + 1)
    }
  }, [startDate, endDate])

  useEffect(() => {
    if (isSupervisor(user) && user.school !== null) {
      fetchSchoolOptions(user, user.school).then(response => {
        response.data.map(schoolOption => {
          if (schoolOption.optionType.type === SUPERVISORS_EDIT_SCHEDULE) {
            setSupervisorsEditSchedule(schoolOption.optionEnabled)
          }
        })
      })
    }
  }, [user])

  const handleOpenChatForInternship = () => {
    onOpenChatForInternship()
  }

  const handleShiftsWidgetCoverClick = (e) => {
    if (inWidgetMode) {
      onShiftsWidgetCoverClick(e)
    }
  }

  const toggleColumnSelect = columnIndex => {
    if (multiSelectionMode) {
      addColumnShiftsToMultiSelection(columnIndex)
    }
  }

  const addColumnShiftsToMultiSelection = columnIndex => {
    const columnDate = startDate.clone().add(columnIndex, 'days')
    const shiftsToAdd = []

    internships.forEach((internship, rowIndex) => {
      if (!canOnlyEditEventCode(user, internship)) {
        const shiftOfTheDay = getShiftByDayAndInternship(internship, columnDate)

        shiftsToAdd.push(getMultiSelectionShiftEncapsulation(
          createShiftOrConfigureIt(shiftOfTheDay, internship.id, columnDate.clone(), columnDate.clone()),
          rowIndex,
          columnIndex,
          internship
        ))
      }
    })

    if (shiftsToAdd.length > 0) {
      multiSelectionAddingHandler(shiftsToAdd, COLUMN)
    }
  }

  const renderTableHeader = () => {
    if (startDate && endDate) {
      const currentDate = moment(startDate).locale(LanguageTags.EN)
      const dates = []

      for (let i = 0; i < numberOfDays; i++) {
        const dateDayToString = t(currentDate.format('dddd'))
        const name = dateDayToString[0].toUpperCase() + dateDayToString[1] + '.'
        const dateAsString = currentDate.format(DATE_FORMAT_TYPE)

        dates.push({ name, date: dateAsString })

        currentDate.add(1, 'days')
      }

      return (
        <thead>
          <tr>
            <th>
              <div className='table-first-header'>
                <b>
                  {t('Internships')} {selected.length > 0 && `(${selected.length})`}
                </b>
                {!isReadOnly && !inWidgetMode && (
                  <LeftFloatingCheckbox
                    checked={selected.length === internships.length && selected.length > 0}
                    onChange={() => setSelected(selected.length === internships.length ? [] : [...internships])}
                  />
                )}
              </div>
            </th>
            {renderTableHeaderCells(dates)}
          </tr>
        </thead>
      )
    }
  }

  const renderTableHeaderCells = dates => {
    const CellType = getCellByAppState()

    return dates.map((d, index) => {
      const pauseQuotasData = getPauseQuotasCounts[index]
      const shiftsData = getShiftsCounts[index]
      const key = index + '_' + d.name
      const sharedCellProps = {
        name: d.name,
        date: d.date,
        onClick: () => toggleColumnSelect(index)
      }

      if (pauseQuotasData) {
        return (
          <CellType
            key={key}
            {...sharedCellProps}
            total={pauseQuotasData.total}
            shiftsTotal={shiftsData.total}
            details={pauseQuotasData.details}
            shiftsDetails={shiftsData.details}
          />
        )
      }

      return (
        <CellType
          key={key}
          {...sharedCellProps}
        />
      )
    })
  }

  const getCellByAppState = () => {
    if (isObject(getSelectedSector) && getSelectedSector.id > -1) {
      return getSelectedSector.pausesEnabled
        ? PausesHeaderCell
        : TotalHeaderCell
    }

    return SimpleHeaderCell
  }

  const renderTableBody = () => {
    const rows = []

    if (isArray(internships) && numberOfDays) {
      internships.forEach((i, index) => {
        rows.push(
          <Line
            key={'internship-' + i.id}
            internship={i}
            internshipIndex={index}
            startDate={startDate}
            numberOfDays={numberOfDays}
            selectedShifts={selectedShifts}
            userIsNurse={isNurse(user)}
            setIntershipToValidate={setIntershipToValidate}
            onClickedDraft={onClickedDraft}
            onDeleteSchedule={onDeleteSchedule}
            onStudentSelected={onStudentSelected}
            onInternshipSelected={onInternshipSelected}
            multiSelectionMode={multiSelectionMode}
            inWidgetMode={inWidgetMode}
            onOpenChatForInternship={handleOpenChatForInternship}
            addToMultiSelection={addToMultiSelection}
            removeFromMultiSelection={removeFromMultiSelection}
            multiSelectionAddingHandler={multiSelectionAddingHandler}
            onDesiderataSelect={onDesiderataSelect}
            supervisorsEditSchedule={supervisorsEditSchedule}
          />
        )
      })
    }

    return (
      <tbody>
        {rows}
      </tbody>
    )
  }

  const renderContainer = () => {
    if (loading) {
      return getLoadingAnimation()
    }

    return (
      <table
        className={multiSelectionMode ? 'table-in-multi-select-mode' : ''}
        onClick={handleShiftsWidgetCoverClick}
      >
        {renderTableHeader()}
        {renderTableBody()}
      </table>
    )
  }

  const getLoadingAnimation = () => {
    return (
      <div className='fill-content'>
        <div className='table-loading-ring' />
      </div>
    )
  }

  return renderContainer()
}

export default connect(mapStateToProps, mapDispatchToProps)(InternshipsShiftsEditor)
